import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import styles from "./SliderManagement.module.css";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiService } from "../../services/api";
import { Slider } from "../../types";
import { useApp } from "../../contexts/AppContext";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Sortable SliderCard component with drag and drop support
const SortableSliderCard = ({
  slider,
  onEdit,
  onToggleActive,
  onDelete,
}: {
  slider: Slider;
  onEdit: (slider: Slider) => void;
  onToggleActive: (slider: Slider) => void;
  onDelete: (id: string) => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: slider.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Memoize video event handlers to prevent recreation on every render
  const handleVideoLoadStart = useCallback(() => {
    // Removed console.log to prevent re-renders
  }, []);

  const handleVideoLoadedMetadata = useCallback(
    (e: React.SyntheticEvent<HTMLVideoElement>) => {
      const video = e.target as HTMLVideoElement;
      // Устанавливаем время на 1 секунду для показа превью
      video.currentTime = 1;
    },
    []
  );

  const handleVideoCanPlay = useCallback(() => {
    // Removed console.log to prevent re-renders
  }, []);

  const handleVideoError = useCallback(
    (e: React.SyntheticEvent<HTMLVideoElement>) => {
      const video = e.target as HTMLVideoElement;
      video.style.display = "none";
      const container = video.parentElement;
      if (container) {
        container.innerHTML =
          '<div style="display: flex; align-items: center; justify-content: center; height: 100%; background: #f8f9fa; color: #6c757d; font-size: 14px;">Нет видео</div>';
      }
    },
    []
  );

  const handleImageError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      (e.target as HTMLImageElement).src =
        "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPlRyZWJyIGltYWdlaW4KPC90ZXh0Pjwvc3ZnPg==";
    },
    []
  );

  const handleImageLoad = useCallback(() => {
    // Removed console.log to prevent re-renders
  }, []);

  // Memoize the media URL with cache busting
  const mediaUrl = useMemo(() => {
    return `${slider.url}?v=${Date.now()}`;
  }, [slider.url]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.sliderCard} ${isDragging ? styles.dragging : ""}`}
    >
      <div className={styles.dragHandle} {...attributes} {...listeners}>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="7" cy="5" r="1.5" fill="currentColor" />
          <circle cx="13" cy="5" r="1.5" fill="currentColor" />
          <circle cx="7" cy="10" r="1.5" fill="currentColor" />
          <circle cx="13" cy="10" r="1.5" fill="currentColor" />
          <circle cx="7" cy="15" r="1.5" fill="currentColor" />
          <circle cx="13" cy="15" r="1.5" fill="currentColor" />
        </svg>
      </div>
      <div className={styles.mediaContainer}>
        {slider.type === "IMAGE" ? (
          <img
            className={styles.mediaPreview}
            src={mediaUrl}
            alt="Slider"
            onError={handleImageError}
            onLoad={handleImageLoad}
          />
        ) : (
          <video
            className={styles.videoPreview}
            src={mediaUrl}
            controls
            controlsList="nodownload noremoteplayback noplaybackrate"
            disablePictureInPicture
            preload="metadata"
            onLoadStart={handleVideoLoadStart}
            onLoadedMetadata={handleVideoLoadedMetadata}
            onCanPlay={handleVideoCanPlay}
            onError={handleVideoError}
          />
        )}
      </div>
      <div className={styles.cardContent}>
        {/* <h3 className={styles.cardTitle}>{slider.title || "Без названия"}</h3> */}
        <span
          className={`${styles.cardType} ${
            slider.type === "IMAGE" ? styles.typeImage : styles.typeVideo
          }`}
        >
          {slider.type === "IMAGE" ? "Изображение" : "Видео"}
        </span>
        <div className={styles.cardOrder}>Порядок: {slider.order}</div>
        <div className={styles.cardActions}>
          <button
            className={styles.actionButton}
            onClick={() => onEdit(slider)}
          >
            Редактировать
          </button>
          {/* <button
            className={`${styles.actionButton} ${styles.actionWarning}`}
            onClick={() => onToggleActive(slider)}
          >
            {slider.isActive ? "Деактивировать" : "Активировать"}
          </button> */}
          <button
            className={`${styles.actionButton} ${styles.actionDanger}`}
            onClick={() => onDelete(slider.id)}
          >
            Удалить
          </button>
        </div>
      </div>
    </div>
  );
};

export default function SliderManagement() {
  const { refreshAppSettings } = useApp();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSlider, setEditingSlider] = useState<Slider | null>(null);
  const [formData, setFormData] = useState({
    title: "",
    url: "",
    type: "IMAGE" as "IMAGE" | "VIDEO",
    order: 0,
    isActive: true,
  });
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [formError, setFormError] = useState<string>("");
  const [settingsData, setSettingsData] = useState<{
    sliderSlideDelay?: number;
  }>({
    sliderSlideDelay: 5,
  });
  const [settingsInitialized, setSettingsInitialized] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Check if user is authenticated
  React.useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Необходимо войти в систему для управления настройками");
    }
  }, []);

  const { data: slidersData, isLoading } = useQuery({
    queryKey: ["sliders"],
    queryFn: () => apiService.getSliders(),
  });

  // Sort sliders by order to ensure correct display order
  const sliders = useMemo(() => {
    if (!slidersData) return undefined;
    return [...slidersData].sort((a, b) => a.order - b.order);
  }, [slidersData]);

  const { data: appSettings } = useQuery({
    queryKey: ["appSettings"],
    queryFn: () => apiService.getAppSettings(),
  });

  const createMutation = useMutation({
    mutationFn: apiService.createSlider,
    onSuccess: (data) => {
      console.log("Slider created successfully:", data);
      queryClient.invalidateQueries({ queryKey: ["sliders"] });
      setIsModalOpen(false);
      resetForm();
      setSuccess("Слайдер успешно создан!");
    },
    onError: (error: any) => {
      console.error("Create slider error:", error);
      const validationErrors = error?.response?.data?.errors;
      if (validationErrors && Array.isArray(validationErrors) && validationErrors.length > 0) {
        const errorMessages = validationErrors.map((err: any) => {
          const fieldName = err.param || "Поле";
          const message = err.msg || "Ошибка валидации";
          return `• ${fieldName}: ${message}`;
        });
        setFormError(`Ошибки валидации:\n${errorMessages.join("\n")}`);
      } else {
        setFormError(error.response?.data?.message || "Ошибка при создании слайдера");
      }
    },
  });

  // Mutation for updating multiple sliders order
  const updateOrderMutation = useMutation({
    mutationFn: async (sliders: { id: string; order: number }[]) => {
      // Update each slider's order
      const promises = sliders.map(({ id, order }) =>
        apiService.updateSlider(id, { order })
      );
      await Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sliders"] });
      setSuccess("Порядок слайдеров успешно обновлен!");
    },
    onError: (error: any) => {
      setError(
        error.response?.data?.message ||
          "Ошибка при обновлении порядка слайдеров"
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Slider> }) =>
      apiService.updateSlider(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sliders"] });
      setIsModalOpen(false);
      resetForm();
      setSuccess("Слайдер успешно обновлен!");
    },
    onError: (error: any) => {
      const validationErrors = error?.response?.data?.errors;
      if (validationErrors && Array.isArray(validationErrors) && validationErrors.length > 0) {
        const errorMessages = validationErrors.map((err: any) => {
          const fieldName = err.param || "Поле";
          const message = err.msg || "Ошибка валидации";
          return `• ${fieldName}: ${message}`;
        });
        setFormError(`Ошибки валидации:\n${errorMessages.join("\n")}`);
      } else {
        setFormError(error.response?.data?.message || "Ошибка при обновлении слайдера");
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: apiService.deleteSlider,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sliders"] });
      setSuccess("Слайдер успешно удален!");
    },
    onError: (error: any) => {
      setError(error.response?.data?.message || "Ошибка при удалении слайдера");
    },
  });

  const uploadMutation = useMutation({
    mutationFn: apiService.uploadSliderMedia,
    onSuccess: (data) => {
      // Extract relative path from URL if it's a full URL
      const relativeUrl = data.url.startsWith("http")
        ? data.url.replace(/https?:\/\/[^/]+/, "")
        : data.url;
      setFormData((prev) => ({ ...prev, url: relativeUrl }));
      setSuccess("Файл успешно загружен!");
      setError("");
    },
    onError: (error: any) => {
      setError(error.response?.data?.message || "Ошибка при загрузке файла");
    },
  });

  const updateSettingsMutation = useMutation({
    mutationFn: apiService.updateAppSettings,
    onSuccess: async (data) => {
      // Обновляем локальное состояние с сохраненными значениями (конвертируем в секунды)
      if (data) {
        setSettingsData({
          sliderSlideDelay: Math.round(data.sliderSlideDelay / 1000),
        });
        setSettingsInitialized(true);
      }
      queryClient.invalidateQueries({ queryKey: ["appSettings"] });
      setSuccess("Настройки успешно обновлены!");
      setError("");
      // Обновляем настройки в контексте приложения
      await refreshAppSettings();
    },
    onError: (error: any) => {
      console.error("Settings update error in SliderManagement:", error);
      console.error("Error response:", error.response);
      console.error("Error message:", error.message);
      setError(
        error.response?.data?.message || "Ошибка при обновлении настроек"
      );
    },
  });

  const resetForm = () => {
    setFormData({
      title: "",
      url: "",
      type: "IMAGE",
      order: 0,
      isActive: true,
    });
    setUploadedFile(null);
    setPreviewUrl("");
    setError("");
    setSuccess("");
    setFormError("");
  };

  // Load settings when component mounts (only once)
  React.useEffect(() => {
    if (appSettings && !settingsInitialized) {
      // Конвертируем миллисекунды в секунды для отображения
      setSettingsData({
        sliderSlideDelay: Math.round(appSettings.sliderSlideDelay / 1000),
      });
      setSettingsInitialized(true);
    }
  }, [appSettings, settingsInitialized]);

  const handleSettingsChange = (field: "sliderSlideDelay", value: string) => {
    // Разрешаем пустые значения во время ввода
    if (value === "") {
      setSettingsData((prev) => ({
        ...prev,
        [field]: undefined,
      }));
      return;
    }
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 0) {
      setSettingsData((prev) => ({
        ...prev,
        [field]: numValue,
      }));
    }
  };

  const handleSaveSettings = () => {
    // Проверяем, что все значения заполнены и валидны
    if (settingsData.sliderSlideDelay === undefined) {
      setError("Пожалуйста, заполните все поля настроек");
      return;
    }
    if (
      settingsData.sliderSlideDelay < 1 ||
      settingsData.sliderSlideDelay > 60
    ) {
      setError("Время переключения слайдов должно быть от 1 до 60 секунд");
      return;
    }
    // Конвертируем секунды в миллисекунды перед отправкой
    updateSettingsMutation.mutate({
      sliderSlideDelay: settingsData.sliderSlideDelay * 1000,
    });
  };

  const handleLogin = () => {
    window.location.href = "/admin/login";
  };

  const handleAddNew = () => {
    resetForm();
    setEditingSlider(null);
    // Set order to max order + 1 or 1 if no sliders exist
    const maxOrder =
      sliders && sliders.length > 0
        ? Math.max(...sliders.map((s) => s.order))
        : 0;
    setFormData((prev) => ({ ...prev, order: maxOrder + 1 }));
    setIsModalOpen(true);
  };

  // Handle drag end event for reordering sliders
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id && sliders) {
      const oldIndex = sliders.findIndex((s) => s.id === active.id);
      const newIndex = sliders.findIndex((s) => s.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newSliders = arrayMove(sliders, oldIndex, newIndex);
        const slidersWithOrder = newSliders.map((slider, index) => ({
          id: slider.id,
          order: index + 1,
        }));

        updateOrderMutation.mutate(slidersWithOrder);
      }
    }
  };

  const handleEdit = useCallback((slider: Slider) => {
    // Extract relative path from URL if it's a full URL for storage in formData
    const relativeUrl = slider.url.startsWith("http")
      ? slider.url.replace(/https?:\/\/[^/]+/, "")
      : slider.url;

    setFormData({
      title: slider.title || "",
      url: relativeUrl,
      type: slider.type,
      order: slider.order,
      isActive: slider.isActive,
    });
    // Use full URL for preview
    setPreviewUrl(slider.url);
    setEditingSlider(slider);
    setIsModalOpen(true);
  }, []);

  const validateFileType = (file: File): boolean => {
    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");

      if (formData.type === "IMAGE" && !isImage) {
      setFormError("Пожалуйста, выберите файл изображения (JPG, PNG, GIF и т.д.)");
      return false;
    }

    if (formData.type === "VIDEO" && !isVideo) {
      setFormError("Пожалуйста, выберите видео файл (MP4, WebM и т.д.)");
      return false;
    }

    return true;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!validateFileType(file)) {
        // Очищаем input, чтобы можно было выбрать файл снова
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      setUploadedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setFormError("");

      // Автоматически загружаем файл при выборе
      uploadMutation.mutate(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    console.log("Form submitted with data:", formData);
    console.log("Upload mutation pending:", uploadMutation.isPending);

    setFormError("");

    const validationMessages: string[] = [];

    if (!formData.url) {
      validationMessages.push("• Файл: необходимо выбрать файл для загрузки или указать URL");
    }

    if (uploadMutation.isPending) {
      validationMessages.push("• Загрузка: дождитесь завершения загрузки файла");
    }

    if (validationMessages.length > 0) {
      setFormError(`Ошибки валидации:\n${validationMessages.join("\n")}`);
      return;
    }

    if (editingSlider) {
      console.log("Updating slider:", editingSlider.id, formData);
      updateMutation.mutate({
        id: editingSlider.id,
        data: formData,
      });
    } else {
      console.log("Creating new slider:", formData);
      createMutation.mutate(formData);
    }
  };

  const handleDelete = useCallback(
    (id: string) => {
      if (window.confirm("Вы уверены, что хотите удалить этот слайдер?")) {
        deleteMutation.mutate(id);
      }
    },
    [deleteMutation]
  );

  const handleToggleActive = useCallback(
    (slider: Slider) => {
      updateMutation.mutate({
        id: slider.id,
        data: { isActive: !slider.isActive },
      });
    },
    [updateMutation]
  );

  useEffect(() => {
    if (isModalOpen && modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
  }, [isModalOpen]);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingSpinner}>Загрузка слайдеров...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.logo}>Управление слайдерами</div>
        <button
          className={styles.backButton}
          onClick={() => window.history.back()}
        >
          Назад
        </button>
      </header>

      <main className={styles.mainContent}>
        <h1 className={styles.pageTitle}>Слайдеры</h1>

        {error && !isModalOpen && (
          <div className={styles.errorMessage}>{error}</div>
        )}
        {success && !isModalOpen && (
          <div className={styles.successMessage}>{success}</div>
        )}

        <div className={styles.settingsSection}>
          <h2 className={styles.settingsTitle}>Настройки времени</h2>
          <div className={styles.settingsGrid}>
            <div className={styles.settingsItem}>
              <label className={styles.settingsLabel}>
                Время переключения слайдов (сек)
              </label>
              <input
                className={styles.settingsInput}
                type="number"
                min="1"
                max="60"
                value={settingsData.sliderSlideDelay ?? ""}
                onChange={(e) =>
                  handleSettingsChange("sliderSlideDelay", e.target.value)
                }
                onBlur={(e) => {
                  // Если поле пустое при потере фокуса, восстанавливаем значение
                  if (
                    e.target.value === "" &&
                    settingsData.sliderSlideDelay === undefined
                  ) {
                    const defaultValue = appSettings?.sliderSlideDelay
                      ? Math.round(appSettings.sliderSlideDelay / 1000)
                      : 5;
                    handleSettingsChange(
                      "sliderSlideDelay",
                      defaultValue.toString()
                    );
                  }
                }}
              />
            </div>
          </div>
          {localStorage.getItem("token") ? (
            <button
              className={styles.saveSettingsButton}
              onClick={handleSaveSettings}
              disabled={updateSettingsMutation.isPending}
            >
              {updateSettingsMutation.isPending
                ? "Сохранение..."
                : "Сохранить настройки"}
            </button>
          ) : (
            <button className={styles.loginButton} onClick={handleLogin}>
              Войти в систему для управления настройками
            </button>
          )}
        </div>

        <button
          className={styles.addButton}
          onClick={handleAddNew}
          type="button"
        >
          + Добавить слайдер
        </button>

        {sliders && sliders.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sliders.map((s) => s.id)}
              strategy={rectSortingStrategy}
            >
              <div className={styles.sliderGrid}>
                {sliders.map((slider) => (
                  <SortableSliderCard
                    key={slider.id}
                    slider={slider}
                    onEdit={handleEdit}
                    onToggleActive={handleToggleActive}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className={styles.emptyState}>Слайдеры отсутствуют</div>
        )}

        <div
          className={`${styles.modal} ${isModalOpen ? styles.modalOpen : ""}`}
        >
          <div
            ref={modalContentRef}
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className={styles.modalTitle}>
              {editingSlider ? "Редактировать слайдер" : "Добавить слайдер"}
            </h2>

            {formError && (
              <div
                className={styles.errorMessage}
                style={{ whiteSpace: "pre-line" }}
              >
                {formError}
              </div>
            )}
            {success && <div className={styles.successMessage}>{success}</div>}

            <form onSubmit={handleSubmit}>
              {/* <div className={styles.formGroup}>
                <label className={styles.label}>Название</label>
                <input
                  className={styles.input}
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, title: e.target.value }))
                  }
                  placeholder="Введите название слайдера"
                />
              </div> */}

              <div className={styles.formGroup}>
                <label className={styles.label}>Тип медиа</label>
                <select
                  className={styles.select}
                  value={formData.type}
                  onChange={(e) => {
                    const newType = e.target.value as "IMAGE" | "VIDEO";
                    setFormData((prev) => ({
                      ...prev,
                      type: newType,
                    }));
                    // Очищаем выбранный файл при изменении типа
                    setUploadedFile(null);
                    setPreviewUrl("");
                    setFormData((prev) => ({ ...prev, url: "" }));
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                >
                  <option value="IMAGE">Изображение</option>
                  <option value="VIDEO">Видео</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Загрузить файл</label>
                <div
                  className={styles.fileUploadArea}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add(styles.dragover);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove(styles.dragover);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove(styles.dragover);
                    const file = e.dataTransfer.files[0];
                    if (file) {
                      if (!validateFileType(file)) {
                        return;
                      }

                      setUploadedFile(file);
                      const url = URL.createObjectURL(file);
                      setPreviewUrl(url);
                      setFormError("");

                      // Автоматически загружаем файл при перетаскивании
                      uploadMutation.mutate(file);
                    }
                  }}
                >
                  <div className={styles.fileUploadText}>
                    {uploadedFile
                      ? `Выбран файл: ${uploadedFile.name}`
                      : "Нажмите или перетащите файл сюда"}
                  </div>
                  <div className={styles.fileUploadSubtext}>
                    {uploadedFile
                      ? "Файл будет загружен автоматически"
                      : formData.type === "IMAGE"
                      ? "Поддерживаются: JPG, PNG, GIF, WebP"
                      : "Поддерживаются: MP4, WebM, MOV"}
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={formData.type === "IMAGE" ? "image/*" : "video/*"}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
                {uploadMutation.isPending && (
                  <div
                    style={{
                      textAlign: "center",
                      margin: "10px 0",
                      color: "#666",
                    }}
                  >
                    Загрузка файла...
                  </div>
                )}
                {previewUrl && formData.type === "IMAGE" && (
                  <img
                    className={styles.previewImage}
                    src={previewUrl}
                    alt="Preview"
                  />
                )}
                {previewUrl && formData.type === "VIDEO" && (
                  <video
                    className={styles.previewImage}
                    src={previewUrl}
                    controls
                    controlsList="nodownload noremoteplayback noplaybackrate"
                    disablePictureInPicture
                    style={{ maxWidth: "100%", maxHeight: "200px" }}
                  />
                )}
              </div>

              <div className={styles.modalActions}>
                <button
                  className={`${styles.actionButton} ${styles.actionWarning}`}
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    setFormError("");
                    setSuccess("");
                  }}
                >
                  Отмена
                </button>
                <button
                  className={styles.actionButton}
                  type="submit"
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? "Сохранение..."
                    : editingSlider
                    ? "Обновить"
                    : "Сохранить"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
